import { useState } from "react";
import { motion } from "framer-motion";
import { Download, Loader2 } from "lucide-react";
import { useLang, EASE } from "@/lib/site";
import { shareQuoteImage } from "@/lib/quoteImage";
import { copy, quotes } from "@/lib/content";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";

export const ShareButton = ({ q, n, lang, testId }) => {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try { await shareQuoteImage(q[lang], q.tag[lang], n); } finally { setBusy(false); }
  };
  return (
    <button data-testid={testId || `quote-download-${n}-button`} onClick={onClick} disabled={busy}
      className="relative inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-amber-200/25 px-4 py-2 text-xs font-medium text-amber-100 transition-[background-color,border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-amber-200/60 hover:bg-amber-300/10 disabled:opacity-60">
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} className="text-amber-300" />}
      {copy[lang].saveStory}
    </button>
  );
};

const QuoteCard = ({ q, i, lang }) => (
  <motion.figure data-testid={`quote-card-${i + 1}`}
    initial={{ opacity: 0, y: 36 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: EASE }} whileHover={{ y: -6 }}
    className="quote-card group relative flex h-full flex-col justify-between overflow-hidden rounded-3xl border border-amber-200/10 bg-[#0D1322] p-7 sm:p-8">
    <span className="font-display pointer-events-none absolute right-5 top-4 text-[6rem] leading-[0.8] text-amber-300/15 transition-colors duration-500 group-hover:text-amber-300/25">”</span>
    <div>
      <span className="font-mono text-xs text-amber-300">{String(i + 2).padStart(2, "0")} / 07</span>
      <blockquote className="font-display mt-5 text-xl sm:text-[1.45rem] leading-snug text-slate-100">{q[lang]}</blockquote>
    </div>
    <figcaption className="mt-8 flex items-center justify-between border-t border-white/5 pt-5">
      <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400">{q.tag[lang]}</span>
      <ShareButton q={q} n={i + 2} lang={lang} />
    </figcaption>
  </motion.figure>
);

export const Quotes = () => {
  const { lang } = useLang();
  const t = copy[lang];
  const [featured, ...rest] = quotes;
  return (
    <section id="kutipan" data-testid="quotes-section" className="relative py-28 lg:py-40">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-7"><Eyebrow>{t.quotesEyebrow}</Eyebrow><SectionTitle lines={t.quotesTitle} testId="quotes-title" /></Reveal>
          <Reveal delay={0.1} className="lg:col-span-5"><p className="text-slate-300 text-base sm:text-lg leading-relaxed lg:pb-3">{t.quotesSub}</p></Reveal>
        </div>
        <Reveal className="mt-16">
          <figure data-testid="quote-featured" className="featured-quote relative overflow-hidden rounded-[2rem] border border-amber-200/20 p-8 sm:p-14 lg:p-20">
            <span className="font-display absolute right-6 top-2 sm:right-12 sm:top-4 text-[9rem] sm:text-[14rem] leading-[0.8] text-amber-300/20">“</span>
            <span className="relative font-mono text-xs text-amber-300">01 / 07 · {featured.tag[lang]}</span>
            <blockquote className="font-display relative mt-6 max-w-5xl text-3xl sm:text-5xl lg:text-[3.6rem] leading-[1.08] text-slate-50">{featured[lang]}</blockquote>
            <figcaption className="relative mt-10 flex flex-wrap items-center justify-between gap-6 text-slate-300">
              <span className="flex items-center gap-4"><span className="h-px w-12 bg-amber-300" /><span className="font-display italic text-xl">Rinda Marbun</span></span>
              <ShareButton q={featured} n={1} lang={lang} />
            </figcaption>
          </figure>
        </Reveal>
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((q, i) => <QuoteCard key={i} q={q} i={i} lang={lang} />)}
        </div>
      </div>
    </section>
  );
};
